import clsx from "clsx";
import { AnimatePresence, m } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { toWorker } from "../util/toWorker.ts";
import { useLocal } from "../util/local.ts";
import { FORCE_GAME_WINNER_OPTIONS } from "../../common/forceGameWinner.ts";
import type { FootballGameWinnerType, ForceOT } from "../../common/types.ts";

type Team = {
	tid: number;
};

type State = undefined | "saving" | "saved" | "error";

const OVERTIME_OPTIONS = [
	{ value: "regular", label: "Regular simulation" },
	{ value: "regulation", label: "End in regulation" },
	{ value: "1", label: "1 OT" },
	{ value: "2", label: "2 OT" },
	{ value: "3", label: "3 OT" },
	{ value: "4", label: "4 OT" },
	{ value: "5", label: "5 OT" },
	{ value: "any", label: "Any OT (legacy)" },
] as const;
const FOOTBALL_OVERTIME_OPTIONS = [
	{
		value: "defensiveRegulation",
		label: "Defensive Slugfest (low scoring game)",
	},
	{
		value: "defensiveOvertime",
		label: "Defensive Slugfest (low scoring game, with OT)",
	},
] as const;
const useSavingState = () => {
	const [state, setState] = useState<State>();
	const timeoutID = useRef<number | undefined>(undefined);

	const wrappedSetState = (state2: State) => {
		clearTimeout(timeoutID.current);

		setState(state2);

		if (state2 === "saved") {
			timeoutID.current = setTimeout(() => {
				setState(undefined);
			}, 1000);
		}
	};

	useEffect(() => {
		return () => {
			clearTimeout(timeoutID.current);
		};
	}, []);

	return [state, wrappedSetState] as const;
};

const style = {
	minWidth: 41,
};

export const ForceWin = ({
	allowTie,
	className,
	game,
}: {
	allowTie: boolean;
	className?: string;
	game: {
		forceWin?: number | "tie";
		forceWinType?: FootballGameWinnerType;
		forceOT?: ForceOT;
		gid: number;
		teams: [Team, Team];
	};
}) => {
	const [state, setState] = useSavingState();
	const [forceWin, setForceWin] = useState(game.forceWin);
	const [forceOT, setForceOT] = useState<ForceOT>(game.forceOT ?? false);
	const [forceWinType, setForceWinType] = useState<
		FootballGameWinnerType | undefined
	>(game.forceWinType);

	const { godMode, teamInfoCache } = useLocal(["godMode", "teamInfoCache"]);

	const allStarGame = game.teams[0].tid === -1 && game.teams[1].tid === -2;
	const tradeDeadline = game.teams[0].tid === -3 && game.teams[1].tid === -3;

	let form = null;
	if (godMode && !tradeDeadline) {
		const id = `force-win-${game.gid}`;

		form = (
			<div className="my-1 d-flex flex-wrap align-items-center gap-1">
				<label className="me-1" htmlFor={id}>
					Force win?
				</label>
				<div className="btn-group">
					{[game.teams[1], game.teams[0]].map(({ tid }, index) => (
						<button
							key={index}
							className={clsx(
								"btn btn-xs",
								tid === forceWin
									? "btn-outline-god-mode"
									: "btn-light-bordered",
							)}
							onClick={async () => {
								try {
									setState("saving");
									const newForceWin = forceWin === tid ? undefined : tid;
									setForceWin(newForceWin);
									if (newForceWin === undefined) {
										setForceWinType(undefined);
									}
									await toWorker("main", "setForceWin", {
										gid: game.gid,
										tidOrTie: newForceWin,
									});
									setState("saved");
								} catch (error) {
									setState("error");
									console.error(error);
								}
							}}
							style={style}
						>
							{allStarGame
								? `AS${tid === -1 ? 2 : 1}`
								: teamInfoCache[tid]?.abbrev}
						</button>
					))}
					{allowTie ? (
						<button
							className={clsx(
								"btn btn-xs",
								"tie" === forceWin
									? "btn-outline-god-mode"
									: "btn-light-bordered",
							)}
							onClick={async () => {
								try {
									setState("saving");
									setForceWin("tie");
									setForceWinType(undefined);
									await toWorker("main", "setForceWin", {
										gid: game.gid,
										tidOrTie: "tie",
									});
								} catch (error) {
									setState("error");
									throw error;
								}
								setState("saved");
							}}
							style={style}
						>
							Tie
						</button>
					) : null}
				</div>
				<label
					className="ms-1 d-flex align-items-center gap-1"
					htmlFor={`force-ot-${game.gid}`}
				>
					Overtime:
					<select
						id={`force-ot-${game.gid}`}
						className="form-select form-select-xs"
						style={{ width: "auto" }}
						disabled={state === "saving"}
						value={
							forceOT === true
								? "any"
								: forceOT === false
									? "regular"
									: forceOT === 0
										? "regulation"
										: String(forceOT)
						}
						onChange={async (event) => {
							try {
								setState("saving");
								const value = event.target.value;
								const newForceOT: ForceOT =
									value === "regular"
										? false
										: value === "any"
											? true
											: value === "regulation"
												? 0
												: value === "defensiveRegulation" ||
													  value === "defensiveOvertime"
													? value
													: (Number.parseInt(value, 10) as 1 | 2 | 3 | 4 | 5);
								await toWorker("main", "setForceWin", {
									gid: game.gid,
									forceOT: newForceOT,
								});
								setForceOT(newForceOT);
								setState("saved");
							} catch (error) {
								setState("error");
								console.error(error);
							}
						}}
					>
						{OVERTIME_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
						{__SPORT === "football"
							? FOOTBALL_OVERTIME_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))
							: null}
					</select>
				</label>
				{__SPORT === "football" ? (
					<label
						className="ms-1 d-flex align-items-center gap-1"
						htmlFor={`force-win-type-${game.gid}`}
					>
						Game-winning play:
						<select
							id={`force-win-type-${game.gid}`}
							className="form-select form-select-xs"
							style={{ width: "auto" }}
							disabled={
								state === "saving" ||
								forceWin === undefined ||
								forceWin === "tie"
							}
							value={forceWinType ?? ""}
							onChange={async (event) => {
								try {
									setState("saving");
									const value = event.target.value as
										| ""
										| FootballGameWinnerType;
									const newForceWinType = value === "" ? null : value;
									await toWorker("main", "setForceWin", {
										gid: game.gid,
										forceWinType: newForceWinType,
									});
									setForceWinType(value === "" ? undefined : value);
									setState("saved");
								} catch (error) {
									setState("error");
									console.error(error);
								}
							}}
						>
							{FORCE_GAME_WINNER_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</label>
				) : null}
				<button
					className="btn btn-xs btn-light-bordered ms-2"
					onClick={async () => {
						const summary = await toWorker(
							"main",
							"generateGameSummary",
							game.gid,
						);
						if (summary) {
							await navigator.clipboard.writeText(summary);
						}
					}}
					title="Copy game summary to clipboard"
					style={{ padding: "2px 6px" }}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="14"
						height="14"
						fill="currentColor"
						viewBox="0 0 16 16"
					>
						<path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z" />
						<path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z" />
					</svg>
				</button>
				<AnimatePresence>
					{state === "saved" ? (
						<m.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0, transition: { duration: 1 } }}
							transition={{ duration: 0.1 }}
						>
							<span className="ms-2 glyphicon glyphicon-ok text-success" />
						</m.div>
					) : null}
				</AnimatePresence>
				{state === "error" ? (
					<span className="ms-2 text-danger">Error</span>
				) : null}
			</div>
		);
	}

	return <div className={className}>{form}</div>;
};
